import {
  DateRangeInput,
  DateField,
  FontColorEnum,
  FormField,
  FormHint,
  FormLabel,
  IconButton,
  IconButtonSizeEnum,
  IconSizeEnum,
  InformationIcon,
  TIcon,
  Tooltip,
  DateRangeObject,
} from '@acceso/design-system';
import { ReactElement, useMemo } from 'react';
import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { formatDateCustom } from '@/helpers/format-date';
import { I18nextTranslationKeys } from '@/i18n/i18next';
import { fromAppWallClockEdge, toAppWallClock } from '@/utils/spain-time';

import { ToolTipContent } from '../ToolTipContent';

type TInputDateProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  required?: boolean;
  className?: string;
  showTimeField?: boolean;
  seconds?: boolean;
  help?: boolean;
  tooltipHelp?: I18nextTranslationKeys;
  tooltipKeyword?: string;
  tooltipModifiers?: Array<'Ctrl' | 'Shift' | 'Alt'>;
  requiredMessage?: string;
  disabled?: boolean;
  showHelpValue?: boolean;
  onClickTooltip?: () => void;
  iconTooltip?: TIcon;
  tooltipDisabled?: boolean;
  validate?: (value: string) => boolean | string;
};

type TAppTzDateEdgeProps = {
  value: Date | string | null | undefined;
  onChange: (value: Date | null | undefined) => void;
  children: (edge: {
    value: Date | null | undefined;
    onChange: (emitted: Date | null | undefined) => void;
  }) => ReactElement;
};

/**
 * Shows the design-system picker the Madrid wall clock of `value` and hands the
 * form back the instant the user picked (see `@/utils/spain-time`). A value the
 * picker returns untouched keeps its original instant.
 */
function AppTzDateEdge({ value, onChange, children }: TAppTzDateEdgeProps) {
  const time = value ? new Date(value).getTime() : null;
  const original = useMemo(() => (time === null ? null : new Date(time)), [time]);
  const passed = useMemo(() => (original ? toAppWallClock(original) : null), [original]);

  const handleChange = (emitted: Date | null | undefined) => {
    onChange(emitted ? fromAppWallClockEdge(emitted, passed, original) : emitted);
  };

  return children({ value: passed ?? (value as null | undefined), onChange: handleChange });
}

export function InputDate<T extends FieldValues>({
  form,
  name,
  required,
  label,
  className,
  showTimeField,
  seconds,
  help = false,
  tooltipHelp,
  tooltipKeyword,
  tooltipModifiers,
  requiredMessage,
  disabled,
  showHelpValue,
  iconTooltip: IconTooltip,
  tooltipDisabled,
  validate,
  onClickTooltip,
}: TInputDateProps<T>) {
  const { t } = useTranslation();
  const { control } = form;
  const labelSuffix = required ? '*' : '';

  return (
    <FormField className={className}>
      {label && (
        <FormLabel htmlFor={name} className="flex gap-2">
          {label}
          {labelSuffix}

          {help && (
            <div className="flex gap-2">
              <Tooltip
                help={
                  <ToolTipContent
                    text={tooltipHelp as I18nextTranslationKeys}
                    keyword={tooltipKeyword || ''}
                    modifiers={tooltipModifiers || []}
                  />
                }
                grow
              >
                <IconButton
                  onClick={onClickTooltip}
                  size={IconButtonSizeEnum.XSSM}
                  className="!bg-accent-active-primary-30"
                  disabled={tooltipDisabled}
                >
                  {IconTooltip ? (
                    <IconTooltip size={IconSizeEnum.CUSTOM} className="h-3 w-3" />
                  ) : (
                    <InformationIcon size={IconSizeEnum.CUSTOM} className="h-3 w-3" />
                  )}
                </IconButton>
              </Tooltip>
            </div>
          )}
        </FormLabel>
      )}

      <Controller
        name={name}
        control={control}
        rules={{
          required: required
            ? { value: true, message: requiredMessage || t('El campo es obligatorio') }
            : false,
          validate: validate,
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <AppTzDateEdge value={value} onChange={onChange}>
            {({ value: pickerValue, onChange: onPickerChange }) => {
              let helpedValue = '';
              if (value) {
                helpedValue = formatDateCustom({ input: value, format: 'yyyy-MM-dd HH:mm:ss' });
              }

              const getDateField = () => (
                <DateField
                  value={pickerValue ?? undefined}
                  onChange={onPickerChange}
                  showTimeField={showTimeField}
                  seconds={seconds}
                  readOnly={false}
                  disabled={disabled}
                  is24HoursFormat
                />
              );
              const getDateRangeInput = () => (
                <DateRangeInput
                  className="w-full"
                  startRange={pickerValue}
                  endRange={pickerValue}
                  position="right"
                  onApply={(dateRange: DateRangeObject) => {
                    onPickerChange(dateRange.start);
                  }}
                  months={1}
                  showLegend={false}
                  showTodayButton
                  isRanged={false}
                  applyOnSelect
                  allowClear
                  applyButton
                  usePortal
                />
              );

              const getDateControl = () =>
                seconds || showTimeField ? getDateField() : getDateRangeInput();

              return (
                <>
                  {showHelpValue ? (
                    <Tooltip help={helpedValue || ''} grow>
                      {getDateControl()}
                    </Tooltip>
                  ) : (
                    getDateControl()
                  )}

                  {error && (
                    <FormHint color={FontColorEnum.Error20} type="error">
                      {error.message}
                    </FormHint>
                  )}
                </>
              );
            }}
          </AppTzDateEdge>
        )}
      />
    </FormField>
  );
}
